import { memo, useCallback, useMemo } from 'react';
import DiscardConfirmation, {
  useDiscardConfirmation,
} from 'components/DiscardConfirmation';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  createField,
  deleteField,
  fieldMappingSelector,
  isSavingSelectedFieldCompareLoading,
  isSavingSelectedFieldLoading,
  onDiscardForm,
  selectedFieldCompareSelector,
  selectedFieldSelector,
  setCurrentFieldCompare,
  setSelectedField,
  updateField,
} from 'slices/field.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import type { TField } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import FieldDetail from './FieldDetail';
import './FieldDrawer.scss';

const DrawerFields = () => {
  const dispatch = useAppDispatch();

  const fieldMap = useAppSelector(fieldMappingSelector);
  const firstDrawer = useAppSelector(selectedFieldSelector);
  const secondDrawer = useAppSelector(selectedFieldCompareSelector);
  const isSavingSelectedField = useAppSelector(isSavingSelectedFieldLoading);
  const isSavingSelectedFieldCompare = useAppSelector(
    isSavingSelectedFieldCompareLoading
  );
  const [
    openDiscardDialog,
    onCloseDiscardDialog,
    confirmFn,
    confirmDiscardChanged,
  ] = useDiscardConfirmation();

  const isFirstSaveDisable = useMemo(
    () =>
      !firstDrawer || checkSavedDisabled(firstDrawer, fieldMap[firstDrawer.id]),
    [firstDrawer, fieldMap]
  );
  const isSecondSaveDisable = useMemo(
    () =>
      !secondDrawer ||
      checkSavedDisabled(secondDrawer, fieldMap[secondDrawer.id]),
    [secondDrawer, fieldMap]
  );

  const setFirstDrawer = useCallback(
    (data?: TField, checkChanged = false) => {
      if (checkChanged && !isFirstSaveDisable) {
        confirmDiscardChanged(() => {
          dispatch(setSelectedField(data));
        });
        return;
      }
      dispatch(setSelectedField(data));
    },
    [isFirstSaveDisable]
  );

  const setSecondDrawer = useCallback(
    (data?: TField, checkChanged = false) => {
      if (checkChanged && !isSecondSaveDisable) {
        confirmDiscardChanged(() => {
          dispatch(setCurrentFieldCompare(data));
        });
        return;
      }
      dispatch(setCurrentFieldCompare(data));
    },
    [isSecondSaveDisable]
  );

  const setFirstResource = useCallback(
    (field: TField) => setFirstDrawer(field),
    [setFirstDrawer]
  );

  const setSecondResource = useCallback(
    (field: TField) => setSecondDrawer(field),
    [setSecondDrawer]
  );

  useSelectResource(
    'integer',
    fieldMap,
    firstDrawer,
    secondDrawer,
    setFirstResource,
    setSecondResource
  );

  const onClose = (isFirstDrawer: boolean) => {
    if (isFirstDrawer) {
      setFirstDrawer(undefined, true);
    } else {
      setSecondDrawer(undefined, true);
    }
  };
  // ----- functions ----- //
  const setDataFirstDrawer = useCallback(
    ({ key, value, obj }: { key?: string; value?: any; obj?: any }) => {
      if (obj) {
        setFirstDrawer({
          ...firstDrawer,
          ...obj,
        });
      } else if (key) {
        setFirstDrawer({
          ...firstDrawer,
          [key]: value,
        });
      }
    },
    [setFirstDrawer, firstDrawer]
  );

  const setDataSecondDrawer = useCallback(
    ({ key, value }: { key: string; value: any }) => {
      setSecondDrawer({
        ...secondDrawer,
        [key]: value,
      });
    },
    [setSecondDrawer, secondDrawer]
  );

  const handleSaveFirstDrawer = async () => {
    if (firstDrawer.id === 0) {
      dispatch(createField(firstDrawer));
    } else {
      dispatch(
        updateField({
          isFirstDrawer: true,
          data: firstDrawer,
        })
      );
    }
  };

  const handleSaveSecondDrawer = async () => {
    dispatch(
      updateField({
        isFirstDrawer: false,
        data: secondDrawer,
      })
    );
  };

  const delFirstDrawer = async () => {
    const delResult = await dispatch(deleteField(firstDrawer)).unwrap();
    if (delResult) {
      setFirstDrawer(undefined);
    }
  };

  const delSecondDrawer = async () => {
    const delResult = await dispatch(deleteField(secondDrawer)).unwrap();
    if (delResult) {
      setSecondDrawer(undefined);
    }
  };
  return (
    <>
      <TEDrawer
        expandWidth={'70vw'}
        firstDrawer={{
          title: firstDrawer?.name,
          content: firstDrawer && (
            <FieldDetail onChange={setDataFirstDrawer} value={firstDrawer} />
          ),
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText('fields_confirmation_delete')}
              handleDelete={delFirstDrawer}
              isLoading={isSavingSelectedField}
              handleDiscard={() =>
                dispatch(
                  onDiscardForm({ isFirstDrawer: true, id: firstDrawer.id })
                )
              }
              handleSave={handleSaveFirstDrawer}
              isSaveDisable={isFirstSaveDisable}
            />
          ),
          drawerOpen: !!firstDrawer,
          setDrawerOpen: (val) => !val && onClose(true),
        }}
        secondDrawer={{
          title: secondDrawer?.name,
          content: secondDrawer && (
            <FieldDetail onChange={setDataSecondDrawer} value={secondDrawer} />
          ),
          drawerOpen: !!secondDrawer,
          setDrawerOpen: (val) => !val && onClose(false),
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText('fields_confirmation_delete')}
              handleDelete={delSecondDrawer}
              isLoading={isSavingSelectedFieldCompare}
              handleDiscard={() =>
                dispatch(
                  onDiscardForm({ isFirstDrawer: false, id: secondDrawer.id })
                )
              }
              handleSave={handleSaveSecondDrawer}
              isSaveDisable={isSecondSaveDisable}
            />
          ),
        }}
        onExpand={() => null}
      />
      <DiscardConfirmation
        open={openDiscardDialog}
        onClose={onCloseDiscardDialog}
        confirmFn={confirmFn}
      />
    </>
  );
};

export default memo(DrawerFields);
